import React, { useState, useEffect } from 'react';
import { post } from '../../services/ApiService';
import { Select, Modal } from 'antd';
const { Option } = Select;

const AddEventForm = ({ selectedDate, onClose, onEventAdded, users, existingEvent, allEvents = [] }) => {
    const seriesInfo = (() => {
        if (!existingEvent || !existingEvent.seriesId) return null;
        const siblings = allEvents.filter((e) => e.seriesId === existingEvent.seriesId);
        if (siblings.length <= 1) return null;
        const anchor = new Date(existingEvent.startDate).getTime();
        const future = siblings.filter((e) => new Date(e.startDate).getTime() >= anchor).length;
        return { total: siblings.length, future, past: siblings.length - future };
    })();
    const [loading, setIsLoading] = useState(false);
    const [delLoading, setIsDelLoading] = useState(false);
    const [showDeleteChoice, setShowDeleteChoice] = useState(false);
    const [pendingScope, setPendingScope] = useState(null);
    const [hoveredOption, setHoveredOption] = useState(null);
    const [eventName, setEventName] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [repeat, setRepeat] = useState('Never');
    const [isOff, setIsOff] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [division, setDivision] = useState('');
    const timeSlots = Array.from({ length: 24 * 2 }, (_, index) => {
        const hours = Math.floor(index / 2);
        const minutes = index % 2 === 0 ? '00' : '30';
        return `${hours.toString().padStart(2, '0')}:${minutes}`;
    });

    useEffect(() => {
        if (existingEvent) {
            setEventName(existingEvent.eventName || '');
            setStartTime(existingEvent.startTime || '');
            setEndTime(existingEvent.endTime || '');
            setRepeat(existingEvent.repeat || 'Never');
            setIsOff(existingEvent.isOff || false);
            setSelectedUsers(existingEvent.userIds || []);
            setStartDate(existingEvent.startDate 
                ? new Date(existingEvent.startDate).toISOString().split('T')[0] 
                : ''
            );
            setDivision(existingEvent.division || ''); 
        } else {
            setEventName('');
            setStartTime('');
            setEndTime('');
            setRepeat('Never');
            setIsOff(false);
            setSelectedUsers([]);
            setStartDate('');
            setDivision(''); 
        }
    }, [existingEvent]);

    const handleEventNameChange = (e) => {
        const value = e.target.value;
        setEventName(value);
        setIsOff(value === 'CLOSE'); // Set isOff to true if 'CLOSE' is selected, false otherwise
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = {
            eventName,
            startDate,
            startTime,
            endTime,
            repeat,
            isOff,
            userIds: selectedUsers,
            eventId: existingEvent?._id,
            division,
        };

        setIsLoading(true);
        const res = existingEvent 
        ? await post('api/shifts/event/update', formData)
        : await post('api/shifts/event/add', formData);
        setIsLoading(false);
        if(res.success){
            setEventName('');
            setStartTime('');
            setEndTime('');
            setRepeat('Never');
            setIsOff(false);
            setSelectedUsers([]);
            setStartDate('');
            setDivision(''); 

            onEventAdded();
            onClose();
        }
    };

    const handleDeleteEvent = () => {
        if (!existingEvent || !existingEvent._id) return;
        if (seriesInfo) {
            setShowDeleteChoice(true);
        } else {
            performDelete();
        }
    };

    const performDelete = async (scope) => {
        if (!existingEvent || !existingEvent._id) return;
        try {
            setPendingScope(scope || 'single');
            setIsDelLoading(true);
            const res = await post('api/shifts/event/delete', {
                eventId: existingEvent._id,
                ...(scope ? { scope } : {}),
            });
            if (res.success) {
                setShowDeleteChoice(false);
                onEventAdded();
                onClose();
            }
        } catch (error) {
            console.error("Error deleting event:", error);
        } finally {
            setIsDelLoading(false);
            setPendingScope(null);
        }
    };

    const deleteOptions = [
        {
            key: 'single',
            danger: false,
            icon: 'bi bi-calendar-event',
            title: 'This occurrence only',
            desc: 'Removes just this date. Other occurrences stay.',
        },
        {
            key: 'future',
            danger: true,
            icon: 'bi bi-calendar-x',
            title: 'This and all future occurrences',
            desc: `Removes ${seriesInfo ? seriesInfo.future : 'all'} occurrence(s) from this date forward. Past stays.`,
        },
    ];

    return (
        <form onSubmit={handleSubmit}>
            {seriesInfo && (
                <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13, borderLeft: '4px solid #3b82f6' }}>
                    <strong>Recurring series</strong> — {seriesInfo.total} total occurrence(s)
                    ({seriesInfo.past} past, {seriesInfo.future} from this date forward).
                    <br />
                    Updates apply to <strong>this and all future occurrences</strong>. When deleting, you can choose <strong>this occurrence only</strong> or <strong>this and all future</strong>. Past occurrences are not affected.
                </div>
            )}
            <div className='col-12 mt-4'>
                <label className="form-label">Event Type</label>
                <select className='form-control' value={eventName} onChange={handleEventNameChange} required>
                    <option value="">Select Event</option>
                    <option value="OPEN">OPEN</option>
                    <option value="CLOSE">CLOSE</option>
                </select>
            </div>
            
            {/* Add Division Field */}
            <div className='col-12 mt-4'>
                <label className="form-label">Division</label>
                <select 
                    className='form-control' 
                    value={division} 
                    onChange={(e) => setDivision(e.target.value)}
                    required
                >
                    <option value="">Select Division</option>
                    <option value="PHARMA">Pharma</option>
                    <option value="CLINICAL">Clinical</option>
                </select>
            </div>

            <div className='col-12 mt-4'>
                <label className="form-label">Date</label>
                <input
                    className='form-control'
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Start date"
                    required
                />
            </div>

            <div className="row mt-4">
                <div className="col-5">
                    <label className="form-label">Start Time</label>
                    <select className='form-control' placeholder="From Time" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                        <option value="">Select Time</option>
                        {timeSlots.map((time) => (
                            <option key={time} value={time}>
                                {time}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-1 d-flex align-items-center justify-content-center">
                    <span style={{fontSize:'30px'}}>-</span>
                </div>
                <div className="col-6">
                    <label className="form-label">End Time</label>
                    <select className='form-control' placeholder="End Time" value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                        <option value="">Select Time</option>
                        {timeSlots.map((time) => (
                            <option key={time} value={time}>
                                {time}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className='col-12 mt-4'>
                <label className="form-label">Repeat</label>
                <select className='form-control' value={repeat} onChange={(e) => setRepeat(e.target.value)}>
                    <option value="Never">Never</option>
                    <option value="Weekly">Weekly</option>
                </select>
            </div>

            <div className='col-12 mt-4 mb-4'>
                <label className="form-label">Users</label>
                <Select 
                    className='form-control' 
                    mode="multiple" 
                    placeholder="Select Users" 
                    value={selectedUsers} 
                    onChange={setSelectedUsers}
                >
                    {users.map(user => (
                        <Option key={user._id} value={user._id}>{user.firstname} {user.lastname}</Option>
                    ))}x`   `
                </Select>
            </div>

            <div className="mt-4">
                <button className='btn btn-primary btn-sm m-2' type="submit" disabled={loading}>
                    {loading && <i className="fa fa-spinner fa-spin"></i>}  {existingEvent ? 'Update Event' : 'Add Event'}
                </button>
                {existingEvent && (
                    <button className='btn btn-danger btn-sm m-2' type="button" onClick={handleDeleteEvent} disabled={delLoading}>
                      {delLoading && <i className='fa fa-spinner fa-spin'></i>}  Delete Event
                    </button>
                )}
            </div>

            <Modal
                open={showDeleteChoice}
                title={null}
                footer={null}
                centered
                width={420}
                closable={!delLoading}
                maskClosable={!delLoading}
                onCancel={() => setShowDeleteChoice(false)}
            >
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                    <span style={{
                        width: 52, height: 52, borderRadius: '50%', background: '#fff1f0',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
                    }}>
                        <i className="fa fa-exclamation-triangle" style={{ color: '#cf1322', fontSize: 20 }} />
                    </span>
                    <h5 style={{ fontWeight: 700, margin: '0 0 4px' }}>Delete recurring event</h5>
                    <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
                        This event repeats. Choose what you want to delete.
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {deleteOptions.map((opt) => {
                        const isHover = hoveredOption === opt.key && !delLoading;
                        const isPending = pendingScope === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                disabled={delLoading}
                                onMouseEnter={() => setHoveredOption(opt.key)}
                                onMouseLeave={() => setHoveredOption(null)}
                                onClick={() => performDelete(opt.key)}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                                    textAlign: 'left', padding: '12px 14px', borderRadius: 10,
                                    border: `1px solid ${opt.danger ? '#ffccc7' : '#e5e7eb'}`,
                                    background: opt.danger
                                        ? (isHover ? '#ffece8' : '#fff5f5')
                                        : (isHover ? '#f5f7fa' : '#ffffff'),
                                    color: opt.danger ? '#cf1322' : '#1f2937',
                                    cursor: delLoading ? 'not-allowed' : 'pointer',
                                    boxShadow: isHover ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                    opacity: delLoading && !isPending ? 0.6 : 1,
                                    transition: 'all .15s ease',
                                }}
                            >
                                <span style={{
                                    width: 36, height: 36, flex: '0 0 36px', borderRadius: 8,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    background: opt.danger ? '#ffe1de' : '#eef1f5',
                                }}>
                                    <i className={isPending ? 'fa fa-spinner fa-spin' : opt.icon} style={{ fontSize: 16 }} />
                                </span>
                                <span style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: 600, fontSize: 14 }}>{opt.title}</span>
                                    <span style={{ fontSize: 12, color: opt.danger ? '#a8324a' : '#6b7280' }}>{opt.desc}</span>
                                </span>
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        onClick={() => setShowDeleteChoice(false)}
                        disabled={delLoading}
                        style={{
                            width: '100%', marginTop: 4, padding: '10px', borderRadius: 10,
                            border: 'none', background: 'transparent', color: '#6b7280',
                            fontWeight: 600, cursor: delLoading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </Modal>
        </form>
    );
};

export default AddEventForm;